import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'jalali-moment';
import { toast } from 'react-toastify';

import { _post } from '../../../utils/network';
import { commafy } from '../../../utils/methods';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const SignalsList = () => {
	const { t } = useTranslation();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('signals'), link: '/signals-list' },
		{ title: t('list') }
	];

	const [signals, setSignals] = useState([]);
	let signalsListData = {
		head: [
			t('creation_date'),
			t('user'),
			t('stock'),
			t('status'),
			t('operation')
		],
		body: signals.map(signal => [
			moment(signal.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			<Link className='' to={`/users-edit/${signal.user_id_information.user_id}`} target="_blank">
				{signal.user_id_information.user_fullname}
			</Link>,
			<>
				{signal.stock_id_information.stock_information.stock_symbol}
				<span> ( <span className='text-danger'>{commafy(signal.loss_point)}</span> : {commafy(signal.start_point)} : <span className='text-success'>{commafy(signal.profit_point)}</span> ) </span>
			</>,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (signal.signal_state === 0 ? 'success' : signal.signal_state === 1 ? 'info' : signal.signal_state === 2 ? 'primary' : signal.signal_state === 3 ? 'secondary' : signal.signal_state === 4 ? 'danger' : signal.signal_state === 5 ? 'warning' : 'warning')}>
				{signal.signal_state === 0 ? t('pending') : signal.signal_state === 1 ? t('confirmed') : signal.signal_state === 2 ? t('active') : signal.signal_state === 3 ? t('closed') : signal.signal_state === 4 ? t('failed') : signal.signal_state === 5 ? t('canceled') : t('canceled_after_confirmation')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/signals-edit/${signal.signal_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', 'rtl', 'rtl', '', 'ltr']
	};
	
	const [pageNumber, setPageNumber] = useState(1);
	const [pageCounter] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [pagesCount, setPagesCount] = useState(0);
	const [searchSignalStatus, setSearchSignalStatus] = useState(null);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchCounter, setSearchCounter] = useState(0);
	const getSignals = async () => {
		let body = {
			stock_id: null,
			user_id: null,
			signal_state: searchSignalStatus,
			page: pageNumber,
			count: pageCounter
		};
		setSearchButtonIsLoading(true);
		let response = await _post('/signals/admin/getAll', body);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.all_signals);
				setSignals(response.data.signals);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const filterSignals = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return 0;
		let status = event.target.searchSignalStatus.value.trim() || null;
		setSearchSignalStatus(status);
		if(pageNumber === 1)
			setSearchCounter(searchCounter + 1);
		else
			setPageNumber(1);
	};

	const signalStatusList = [
		{ key: t('pending'), value: 0 },
		{ key: t('confirmed'), value: 1 },
		{ key: t('active'), value: 2 },
		{ key: t('closed'), value: 3 },
		{ key: t('failed'), value: 4 },
		{ key: t('canceled'), value: 5 },
		{ key: t('canceled_after_confirmation'), value: 6 }
	];

	useEffect(() => {
		setPagesCount(Math.ceil(totalCount / pageCounter));
	}, [totalCount]);
	
	useEffect(() => {
		setSearchCounter(searchCounter + 1);
	}, [pageNumber]);

	useEffect(() => {
		getSignals();
	}, [searchCounter]);
	
	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('search_for')} {t('signals')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterSignals}>
						<div className="row gx-2">
							<FormField type="select" fieldKey="searchSignalStatus" title={t('status')} value={searchSignalStatus} selectItems={signalStatusList} isSearch={true} />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('signals')} pageNumber={pageNumber} pageCounter={pageCounter} totalCount={totalCount} data={signalsListData} />
			<Pagination pagesCount={pagesCount} action={setPageNumber} />
		</>
	);
};

export default SignalsList;
